/**
 * SubTabs — panel-level (secondary) tabs INSIDE a tab body or section.
 *
 * AppShell owns the app-level tab row; whenever a panel needs its own
 * second tier of tabs (e.g. a detail pane switching between "Lines",
 * "History" and "Raw"), use SubTabs instead of hand-rolling a `TabList`
 * plus `activeTab === '…' && …` conditionals.
 *
 * Declarative like `AppShell.tabs`: describe tabs as data, SubTabs renders
 * the `TabList` and routes the active panel. Supports uncontrolled
 * (`defaultTab`) and controlled (`activeTab` + `onTabChange`) usage.
 *
 * @example
 *   <SubTabs
 *     tabs={[
 *       { value: 'lines',   label: t('billing.preview.column.description'), render: () => <LinesPanel /> },
 *       { value: 'history', label: t('billing.history.title'),              render: () => <HistoryPanel /> },
 *     ]}
 *   />
 */
import { useCallback, useState, type ReactElement, type ReactNode } from 'react';
import {
  Tab,
  TabList,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  tabList: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
    overflowX: 'auto',
  },
  panel: {
    paddingTop: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
});

export interface SubTab {
  value: string;
  label: string;
  icon?: ReactElement;
  /** Render the panel body. Called only when this tab is active. */
  render: () => ReactNode;
}

export interface SubTabsProps {
  tabs: SubTab[];
  /** Initial tab (uncontrolled). Defaults to the first tab's value. */
  defaultTab?: string;
  /** Controlled tab value. Pair with `onTabChange`. */
  activeTab?: string;
  onTabChange?: (value: string) => void;
  /** Fluent tab size. Defaults to `small` (secondary tier). */
  size?: 'small' | 'medium';
  /** Optional class for the outer wrapper. */
  className?: string;
}

export function SubTabs({
  tabs,
  defaultTab,
  activeTab: controlledTab,
  onTabChange,
  size = 'small',
  className,
}: SubTabsProps) {
  const styles = useStyles();
  const isControlled = controlledTab !== undefined;
  const [internalTab, setInternalTab] = useState<string>(defaultTab ?? tabs[0]?.value ?? '');
  const currentTab = isControlled ? controlledTab! : internalTab;

  const setCurrentTab = useCallback((value: string) => {
    if (!isControlled) setInternalTab(value);
    onTabChange?.(value);
  }, [isControlled, onTabChange]);

  return (
    <div className={mergeClasses(styles.root, className)}>
      <TabList
        selectedValue={currentTab}
        onTabSelect={(_, data) => setCurrentTab(data.value as string)}
        size={size}
        appearance="subtle"
        className={styles.tabList}
      >
        {tabs.map((tab) => (
          <Tab key={tab.value} value={tab.value} icon={tab.icon}>
            {tab.label}
          </Tab>
        ))}
      </TabList>
      <div className={styles.panel}>
        {tabs.find((tab) => tab.value === currentTab)?.render() ?? null}
      </div>
    </div>
  );
}
